import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { 
  Target, 
  Save, 
  Settings, 
  AlertCircle,
  CheckCircle,
  Percent,
  DollarSign,
  Calculator,
  RefreshCw
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const BaselineManagement = ({ calculationId, currentBaseline, onBaselineUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Manual baseline form state
  const [manualBaseline, setManualBaseline] = useState({
    baseline_name: "Manual Baseline",
    fringe_rate_target: "",
    overhead_rate_target: "",
    ga_rate_target: "",
    fringe_pool_target: "",
    fringe_base_target: "",
    overhead_pool_target: "",
    overhead_base_target: "",
    ga_pool_target: "",
    ga_base_target: ""
  });

  // Variance thresholds state
  const [thresholds, setThresholds] = useState({
    rate_variance_percent: 5.0,
    pool_variance_dollar: 10000.0,
    base_variance_dollar: 10000.0
  });

  useEffect(() => {
    loadThresholds();
  }, []);

  const loadThresholds = async () => {
    try {
      const response = await axios.get(`${API}/variance-thresholds`);
      if (response.data.threshold) {
        setThresholds({
          rate_variance_percent: response.data.threshold.rate_variance_percent || 5.0,
          pool_variance_dollar: response.data.threshold.pool_variance_dollar || 10000.0,
          base_variance_dollar: response.data.threshold.base_variance_dollar || 10000.0
        });
      }
    } catch (e) {
      console.error("Error loading thresholds:", e);
    }
  };

  const handleSetCurrentAsBaseline = async () => {
    if (!calculationId) {
      setError("No calculation selected");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API}/set-baseline-rates`, {
        from_calculation_id: calculationId,
        created_by: "user"
      });

      setSuccess("Current rates have been set as baseline successfully!");
      onBaselineUpdate();
    } catch (e) {
      setError(e.response?.data?.detail || "Error setting current rates as baseline");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManualBaseline = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      const requiredRates = ['fringe_rate_target', 'overhead_rate_target', 'ga_rate_target'];
      const missingRates = requiredRates.filter(field => !manualBaseline[field] || manualBaseline[field] === "");
      
      if (missingRates.length > 0) {
        setError("Please provide all three baseline rates (Fringe, Overhead, G&A)");
        return;
      }

      // Convert string values to numbers
      const baselineData = {
        baseline_name: manualBaseline.baseline_name || "Manual Baseline",
        fringe_rate_target: parseFloat(manualBaseline.fringe_rate_target),
        overhead_rate_target: parseFloat(manualBaseline.overhead_rate_target),
        ga_rate_target: parseFloat(manualBaseline.ga_rate_target),
        fringe_pool_target: manualBaseline.fringe_pool_target ? parseFloat(manualBaseline.fringe_pool_target) : null,
        fringe_base_target: manualBaseline.fringe_base_target ? parseFloat(manualBaseline.fringe_base_target) : null,
        overhead_pool_target: manualBaseline.overhead_pool_target ? parseFloat(manualBaseline.overhead_pool_target) : null,
        overhead_base_target: manualBaseline.overhead_base_target ? parseFloat(manualBaseline.overhead_base_target) : null,
        ga_pool_target: manualBaseline.ga_pool_target ? parseFloat(manualBaseline.ga_pool_target) : null,
        ga_base_target: manualBaseline.ga_base_target ? parseFloat(manualBaseline.ga_base_target) : null,
        created_by: "user"
      };

      await axios.post(`${API}/set-baseline-rates`, baselineData);

      setSuccess("Manual baseline rates saved successfully!");
      onBaselineUpdate();
      
      // Reset form
      setManualBaseline({
        baseline_name: "Manual Baseline",
        fringe_rate_target: "",
        overhead_rate_target: "",
        ga_rate_target: "",
        fringe_pool_target: "",
        fringe_base_target: "",
        overhead_pool_target: "",
        overhead_base_target: "",
        ga_pool_target: "",
        ga_base_target: ""
      });

    } catch (e) {
      setError(e.response?.data?.detail || "Error saving manual baseline");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API}/variance-thresholds`, {
        ...thresholds,
        created_by: "user"
      });

      setSuccess("Variance thresholds updated successfully!");
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving variance thresholds");
    } finally {
      setSaving(false);
    }
  };

  const formatPercentage = (value) => {
    return value || value === 0 ? `${Number(value).toFixed(2)}%` : 'N/A';
  };

  const formatCurrency = (value) => {
    return value || value === 0 ? `$${Number(value).toLocaleString()}` : 'N/A';
  };

  const renderCurrentBaseline = () => {
    if (!currentBaseline) {
      return (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-yellow-800">
            No baseline rates are currently set. Variance monitoring requires baseline rates to compare against.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-700">Active Baseline</h3>
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        </div>
        
        <div className="bg-slate-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-slate-600">G&amp;A Rate</div>
              <div className="text-xl font-bold text-blue-600">
                {formatPercentage(currentBaseline.ga_rate_target)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-slate-600">Overhead Rate</div>
              <div className="text-xl font-bold text-blue-600">
                {formatPercentage(currentBaseline.overhead_rate_target)}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-slate-600">Fringe Rate</div>
              <div className="text-xl font-bold text-green-600">
                {formatPercentage(currentBaseline.fringe_rate_target)}
              </div>
            </div>
          </div>
          
          <div className="text-sm text-slate-600">
            <div><strong>Baseline Name:</strong> {currentBaseline.baseline_name}</div>
            <div><strong>Created:</strong> {new Date(currentBaseline.created_timestamp).toLocaleString()}</div>
            <div><strong>Created By:</strong> {currentBaseline.created_by}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Current Baseline Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-blue-600" />
            <span>Current Baseline</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderCurrentBaseline()}
        </CardContent>
      </Card>

      {/* Set Current as Baseline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="h-5 w-5 text-green-600" />
            <span>Set Current Rates as Baseline</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Use the rates from the currently loaded calculation as your new baseline for variance monitoring.
            This will replace any existing baseline.
          </p>
          <Button 
            onClick={handleSetCurrentAsBaseline}
            disabled={saving || !calculationId}
            className="bg-green-600 hover:bg-green-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {saving ? "Setting Baseline..." : "Set Current as Baseline"}
          </Button>
        </CardContent>
      </Card>

      {/* Manual Baseline Setup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Percent className="h-5 w-5 text-blue-600" />
            <span>Manual Baseline Setup</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-6">
            Manually set baseline target rates based on your DCAA-approved provisional rates or historical averages.
          </p>
          
          <div className="space-y-6">
            {/* Baseline Name */}
            <div>
              <Label htmlFor="baseline_name">Baseline Name</Label>
              <Input
                id="baseline_name"
                placeholder="e.g., FY2024 Provisional Rates"
                value={manualBaseline.baseline_name}
                onChange={(e) => setManualBaseline(prev => ({ ...prev, baseline_name: e.target.value }))}
              />
            </div>

            {/* Rate Targets */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center space-x-2">
                <Percent className="h-4 w-4" />
                <span>Target Rates (%)</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="ga_rate">G&amp;A Rate (%)</Label>
                  <Input
                    id="ga_rate"
                    type="number"
                    step="0.01"
                    placeholder="15.15"
                    value={manualBaseline.ga_rate_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, ga_rate_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="overhead_rate">Overhead Rate (%)</Label>
                  <Input
                    id="overhead_rate"
                    type="number"
                    step="0.01"
                    placeholder="33.44"
                    value={manualBaseline.overhead_rate_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, overhead_rate_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="fringe_rate">Fringe Rate (%)</Label>
                  <Input
                    id="fringe_rate"
                    type="number"
                    step="0.01"
                    placeholder="14.44"
                    value={manualBaseline.fringe_rate_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, fringe_rate_target: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Pool Targets (Optional) */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center space-x-2">
                <DollarSign className="h-4 w-4" />
                <span>Target Pool Amounts ($) - Optional</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="ga_pool">G&amp;A Pool ($)</Label>
                  <Input
                    id="ga_pool"
                    type="number"
                    placeholder="500000"
                    value={manualBaseline.ga_pool_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, ga_pool_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="overhead_pool">Overhead Pool ($)</Label>
                  <Input
                    id="overhead_pool"
                    type="number"
                    placeholder="1200000"
                    value={manualBaseline.overhead_pool_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, overhead_pool_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="fringe_pool">Fringe Pool ($)</Label>
                  <Input
                    id="fringe_pool"
                    type="number"
                    placeholder="800000"
                    value={manualBaseline.fringe_pool_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, fringe_pool_target: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Base Targets (Optional) */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center space-x-2">
                <DollarSign className="h-4 w-4" />
                <span>Target Base Amounts ($) - Optional</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="ga_base">G&amp;A Base ($)</Label>
                  <Input
                    id="ga_base"
                    type="number"
                    placeholder="3300000"
                    value={manualBaseline.ga_base_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, ga_base_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="overhead_base">Overhead Base ($)</Label>
                  <Input
                    id="overhead_base"
                    type="number"
                    placeholder="3600000"
                    value={manualBaseline.overhead_base_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, overhead_base_target: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="fringe_base">Fringe Base ($)</Label>
                  <Input
                    id="fringe_base"
                    type="number"
                    placeholder="5500000"
                    value={manualBaseline.fringe_base_target}
                    onChange={(e) => setManualBaseline(prev => ({ ...prev, fringe_base_target: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <Button 
              onClick={handleSaveManualBaseline}
              disabled={saving}
              className="w-full"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving Baseline..." : "Save Manual Baseline"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Variance Thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-orange-600" />
            <span>Variance Thresholds</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-6">
            Configure when variance alerts should be triggered. Alerts are generated when actual rates exceed these thresholds.
          </p>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="rate_threshold">Rate Variance Threshold (%)</Label>
                <Input
                  id="rate_threshold"
                  type="number"
                  step="0.1"
                  value={thresholds.rate_variance_percent}
                  onChange={(e) => setThresholds(prev => ({ ...prev, rate_variance_percent: parseFloat(e.target.value) }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Alert when rate variance exceeds ±{thresholds.rate_variance_percent}%
                </p>
              </div>
              
              <div>
                <Label htmlFor="pool_threshold">Pool Variance Threshold ($)</Label>
                <Input
                  id="pool_threshold"
                  type="number"
                  step="1000"
                  value={thresholds.pool_variance_dollar}
                  onChange={(e) => setThresholds(prev => ({ ...prev, pool_variance_dollar: parseFloat(e.target.value) }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Alert when pool change exceeds ±${thresholds.pool_variance_dollar?.toLocaleString()}
                </p>
              </div>
              
              <div>
                <Label htmlFor="base_threshold">Base Variance Threshold ($)</Label>
                <Input
                  id="base_threshold"
                  type="number"
                  step="1000"
                  value={thresholds.base_variance_dollar}
                  onChange={(e) => setThresholds(prev => ({ ...prev, base_variance_dollar: parseFloat(e.target.value) }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Alert when base change exceeds ±${thresholds.base_variance_dollar?.toLocaleString()}
                </p>
              </div>
            </div>

            <Button 
              onClick={handleSaveThresholds}
              disabled={saving}
              variant="outline"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving Thresholds..." : "Save Thresholds"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BaselineManagement;